import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LOG_LEVEL } from '../../enums/log-level.enum';
import { LogInterface } from '../../interfaces/log.interface';
import { LogDexieService } from './log-dexie.service';
import { safeStringify } from '../../../../../pace-components/src/lib/utils/safe-stringify.util';

@Injectable()
export class LogIndexedDbService implements LogInterface {
    public constructor(
        private readonly dexieLogger: LogDexieService,
        private readonly loggerService: NGXLogger,
    ) {}

    public prefix(prefix: string): LogInterface {
        this.warn(`Calls to log.prefix are no longer supported! (Desired prefix was: ${prefix})`);
        return this;
    }

    public debug(message: string, ...additional: unknown[]): void {
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                LOG_LEVEL.DEBUG,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log debug message to IndexedDb: ${error}`);
            });

        this.loggerService.debug(message, ...additional);
    }

    public info(message: string, ...additional: unknown[]): void {
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                LOG_LEVEL.INFO,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log info message to IndexedDb: ${error}`);
            });
        this.loggerService.info(message, ...additional);
    }

    public log(message: string, ...additional: unknown[]): void {
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                LOG_LEVEL.NONE,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log log message to IndexedDb: ${error}`);
            });
        this.loggerService.log(message, ...additional);
    }

    public warn(message: string, ...additional: unknown[]): void {
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                LOG_LEVEL.WARN,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log warning message to IndexedDb: ${error}`);
            });
        this.loggerService.warn(message, ...additional);
    }

    public error(message: string, ...additional: unknown[]): void {
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                LOG_LEVEL.ERROR,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log error message to IndexedDb: ${error}`);
            });
        this.loggerService.error(message, ...additional);
    }

    public getLogs(): Observable<string> {
        return from(this.dexieLogger.getAllLogEntries()).pipe(
            map((logEntries) => {
                const textEntries = logEntries.map((logEntry) => logEntry.toString());
                return textEntries.join('\n');
            }),
        );
    }

    private static mergeMessageAndAdditionalInfoToString(message: string, ...additional: string[]): string {
        if (!additional) {
            additional = [];
        }
        additional.splice(0, 0, message);
        return additional.join(' ');
    }
}
